/**
 * MentionTextarea.js
 * Textarea with @-mention support: typing "@" opens a dropdown of org users
 * (server-side search via GET /comments/mention-candidates), selecting one
 * inserts "@Name " and records {user_id, name} for the parent to submit.
 *
 * The dropdown is a plain absolutely-positioned list (no Popover/cmdk) so the
 * textarea never loses focus; keyboard nav is handled in onKeyDown.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { UserX } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import api from "../../lib/api";

const MENTION_DEBOUNCE_MS = 250;

/**
 * If the caret sits at the end of an @-token, return { query, start } where
 * `start` is the index of the "@"; otherwise null. The token starts at "@"
 * preceded by start-of-text or whitespace and runs to the caret (max 30
 * chars, no "@" or newline inside — names with spaces stay matchable).
 */
export function mentionTokenAtCaret(text, caretPos) {
  const before = (text || "").slice(0, caretPos);
  const match = before.match(/(^|\s)@([^@\n]{0,30})$/);
  if (!match) return null;
  return { query: match[2], start: caretPos - match[2].length - 1 };
}

/**
 * Keep only mentions whose "@Name" text still appears in the body — deleting
 * the inserted "@Name" un-tags the user at submit time.
 */
export function survivingMentions(body, mentions) {
  const text = body || "";
  return (mentions || []).filter((m) => m.name && text.includes(`@${m.name}`));
}

const MentionTextarea = ({
  value,
  onChange,
  mentions,
  onMentionsChange,
  projectId,
  placeholder = "Write a comment… use @ to mention someone",
  disabled = false,
  rows = 2,
  onSubmitShortcut = null, // Cmd/Ctrl+Enter
}) => {
  const textareaRef = useRef(null);
  const [token, setToken] = useState(null); // { query, start } | null
  const [candidates, setCandidates] = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const fetchSeq = useRef(0);

  // Debounced server search whenever the @-token query changes.
  useEffect(() => {
    if (token === null || !projectId) {
      setCandidates([]);
      return undefined;
    }
    const seq = ++fetchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/comments/mention-candidates", {
          params: { project_id: projectId, q: token.query },
        });
        if (seq === fetchSeq.current) {
          setCandidates(res.data || []);
          setHighlightIdx(0);
        }
      } catch {
        if (seq === fetchSeq.current) setCandidates([]);
      }
    }, MENTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [token?.query, token !== null, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshToken = useCallback(
    (text, caretPos) => setToken(mentionTokenAtCaret(text, caretPos)),
    []
  );

  const handleChange = (e) => {
    onChange(e.target.value);
    refreshToken(e.target.value, e.target.selectionStart);
  };

  const closeDropdown = () => {
    setToken(null);
    setCandidates([]);
  };

  const selectCandidate = (candidate) => {
    if (!token) return;
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : value.length;
    const inserted = `@${candidate.name} `;
    const next = value.slice(0, token.start) + inserted + value.slice(caret);
    onChange(next);
    if (!(mentions || []).some((m) => m.user_id === candidate.id)) {
      onMentionsChange([...(mentions || []), { user_id: candidate.id, name: candidate.name }]);
    }
    closeDropdown();
    // Restore focus + caret just after the inserted mention.
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const pos = token.start + inserted.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const dropdownOpen = token !== null && candidates.length > 0;

  const handleKeyDown = (e) => {
    if (dropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => (i + 1) % candidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => (i - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        selectCandidate(candidates[highlightIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeDropdown();
        return;
      }
    }
    if (onSubmitShortcut && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmitShortcut();
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => refreshToken(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(closeDropdown, 150)} // let candidate onMouseDown land first
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className="resize-none text-sm"
        data-testid="mention-textarea"
      />
      {dropdownOpen && (
        <div
          // Opens ABOVE the textarea: in the bottom panel the composer sits at
          // the panel's bottom edge, so a downward dropdown covers the Post
          // button (verified live). Height is capped so the directory head
          // scrolls instead of spilling past the thread area.
          className="absolute left-0 right-0 bottom-full mb-1 z-50 rounded-md border border-slate-200 bg-white shadow-md max-h-40 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          {candidates.map((candidate, idx) => (
            <button
              key={candidate.id}
              type="button"
              // onMouseDown beats the textarea's onBlur-close.
              onMouseDown={(e) => {
                e.preventDefault();
                selectCandidate(candidate);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={cn(
                "w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm",
                idx === highlightIdx ? "bg-slate-100" : "bg-white"
              )}
              data-testid={`mention-candidate-${candidate.id}`}
            >
              <span className="min-w-0">
                <span className="font-medium text-slate-800">{candidate.name}</span>
                <span className="ml-2 text-xs text-slate-400 truncate">{candidate.email}</span>
              </span>
              {!candidate.has_project_access && (
                <span
                  className="flex items-center gap-1 text-[11px] text-amber-600 shrink-0"
                  title="No access to this project — they'll get a notification without the comment content"
                >
                  <UserX className="h-3 w-3" />
                  no project access
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
